import { FormattedMessage } from '@liveauctioneers/hammer-ui-core/intl';
import { H5 } from '@liveauctioneers/hammer-ui-core/text';
import { useEffect, useRef } from 'react';
import scrollIntoView from 'scroll-into-view';

import { PaymentMethods, PaymentProviders } from '@/enums';
import { ShippingMethods } from '@/enums/shippingMethod';
import useIsWiderThan from '@/hooks/useIsWiderThan';
import { mergedCaterwaulAndHammerUIThemes } from '@/theme/mergedCaterwaulAndHammerUIThemes';
import { InvoiceItem, OtherCharge, Payment } from '@/types/Invoice';

import { InvoiceSummaryCharges } from './InvoiceSummaryCharges';
import { InvoiceSummaryItems } from './InvoiceSummaryItems';
import { InvoiceSummaryPayments } from './InvoiceSummaryPayments';
import { InvoiceSummaryPayNow } from './InvoiceSummaryPayNow';

type Props = {
    activeProcessingFee: number; //cents
    amountToPay: number; //cents
    amountToPayZip: number; //cents
    bidderPaysProcessingFee: boolean;
    buyersPremium: number;
    catalogId: number;
    currency: string;
    handleSubmitClick: () => void;
    hideShipping: boolean;
    houseShippingQuote: number;
    installmentFee: number;
    invoiceId: number;
    invoiceTotal: number;
    itemTotal: number;
    items: InvoiceItem[];
    otherCharges: OtherCharge[];
    paymentMethod: PaymentMethods;
    paymentProvider: PaymentProviders;
    payments: Payment[];
    paymentsTotal: number;
    readOnly: boolean;
    readyToPay: boolean;
    salesTax: number;
    shipping: number;
    shippingMethod: ShippingMethods;
};

export const InvoiceSummary = ({
    activeProcessingFee,
    amountToPay,
    amountToPayZip,
    bidderPaysProcessingFee,
    buyersPremium,
    catalogId,
    currency = 'USD',
    handleSubmitClick,
    hideShipping,
    houseShippingQuote,
    installmentFee,
    invoiceId,
    invoiceTotal,
    items,
    itemTotal,
    otherCharges = [],
    paymentMethod,
    paymentProvider,
    payments = [],
    paymentsTotal,
    readOnly,
    readyToPay,
    salesTax,
    shipping,
    shippingMethod,
}: Props) => {
    const {
        breakpoints: { tabletWidthNum },
    } = mergedCaterwaulAndHammerUIThemes;
    const showInvoiceSummaryItems = useIsWiderThan(tabletWidthNum);
    const summaryRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (readyToPay && !readOnly) {
            scrollIntoView(summaryRef.current, { align: { top: 0 } });
        }
    });

    return (
        <section
            className="bg-background-tertiary p-lg smMax:px-sm smMax:py-lg"
            ref={summaryRef}
        >
            <div className="mb-lg">
                <H5>
                    <FormattedMessage id="checkout.invoice_summary.title" />
                </H5>
            </div>
            {showInvoiceSummaryItems && (
                <InvoiceSummaryItems
                    currency={currency}
                    items={items}
                />
            )}
            <InvoiceSummaryCharges
                activeProcessingFee={activeProcessingFee}
                bidderPaysProcessingFee={bidderPaysProcessingFee}
                buyersPremium={buyersPremium}
                catalogId={catalogId}
                currency={currency}
                hideShipping={hideShipping}
                houseShippingQuote={houseShippingQuote}
                installmentFee={installmentFee}
                invoiceId={invoiceId}
                invoiceTotal={invoiceTotal}
                itemTotal={itemTotal}
                otherCharges={otherCharges}
                paymentMethod={paymentMethod}
                paymentProvider={paymentProvider}
                payments={payments}
                readOnly={readOnly}
                salesTax={salesTax}
                shipping={shipping}
            />
            {Boolean(payments.length) && (
                <InvoiceSummaryPayments
                    currency={currency}
                    payments={payments}
                    paymentsTotal={paymentsTotal}
                />
            )}
            <InvoiceSummaryPayNow
                amountToPay={amountToPay}
                amountToPayZip={amountToPayZip}
                catalogId={catalogId}
                currency={currency}
                handleSubmitClick={handleSubmitClick}
                readOnly={readOnly}
                readyToPay={readyToPay}
                shippingMethod={shippingMethod}
            />
        </section>
    );
};
